import { useEffect, useState } from "react";
import {
  formatSessionOccupiedError,
  getAllErrorsAsString,
  isSessionOverlapError,
  ServicesURL,
} from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { isTrueValue } from "@utils/utilsValidators";
import { Association } from "src/interfaces/Common";
import { SessionGetInterface } from "@components/EditionFormCst/types";
import { useTranslation } from "react-i18next";

export const useAddEditPhysicalEdition = (
  triggetEdition: (input: any) => any,
  id?: string,
  dataDetail?: any,
  mutate?: any,
  activityDuration?: number,
  mutateSession?: any,
) => {
  dayjs.extend(customParseFormat);
  const { t } = useTranslation();
  const [openConfirmStatus, setOpenConfirmStatus] = useState<{
    s: StatusEnum;
    message?: string;
  }>();
  const [openAssociations, setOpenAssociations] = useState<
    boolean | Association
  >(false);
  const [activitiesAssociation, setActivitiesAssociation] = useState<any>([]);
  const [siteAssociation, setSiteAssociation] = useState<any>([]);
  const [roomAssociation, setRoomAssociation] = useState([]);
  const [facultiesAssociation, setFacultiesAssociation] = useState<any>([]);
  const [tutorsAssociation, setTutorsAssociation] = useState<any>([]);
  const [hasDifferentDuration, setHasDifferentDuration] = useState(false);
  const [savedInputForm, setSavedInputForm] = useState(null);
  const [savedSessions, setSavedSessions] = useState<SessionGetInterface[]>([]);

  useEffect(() => {
    if (dataDetail) {
      dataDetail?.learning_objects &&
        setActivitiesAssociation([dataDetail.learning_objects]);
      // dataDetail?.faculties && setFacultiesAssociation(dataDetail.faculties);
      // dataDetail?.site && setSiteAssociation(Array(dataDetail.site));
      // dataDetail?.rooms && setRoomAssociation(dataDetail.rooms);
    }
  }, [dataDetail]);

  const resetSessionAssociations = () => {
    setFacultiesAssociation([]);
    setSiteAssociation([]);
    setRoomAssociation([]);
    setTutorsAssociation([]);
  };

  const setOpenAssociation = (accessorKey: string, value: boolean) => {
    switch (accessorKey) {
      case "learning_objects":
        setOpenAssociations(
          value
            ? {
                accessorKey: "learning_objects",
                mediaType: "learning_objects",
                type: "table",
                titleKey: "activities-association",
                pageId: PagesID["corporate.catalog.activities"],
                service: ServicesURL.getActivities.replace(
                  ":idCorporate",
                  id || "",
                ),
                multiChoice: false,
                defaultFilters: { type: `DA_PHYSICAL_CLASS` },
                manageConfigs: {
                  type: {
                    optionsToDisable: [
                      "DA_VIRTUAL_CLASS",
                      "DA_EXTERNAL_RES",
                      "DA_YOUTUBE",
                      "DA_LINKEDIN",
                      "DA_LTI",
                    ],
                  },
                },
              }
            : value,
        );
        break;
      case "faculties":
        setOpenAssociations(
          value
            ? {
                accessorKey: "faculties",
                mediaType: "faculties",
                type: "table",
                titleKey: "faculties-association",
                pageId: PagesID["corporate.editions.tutors-faculties"],
                service:
                  ServicesURL.faculties + `?corporate_id=${id}&role=TEACHER`,
              }
            : value,
        );
        break;
      case "tutors":
        setOpenAssociations(
          value
            ? {
                accessorKey: "tutors",
                mediaType: "tutors",
                type: "table",
                titleKey: "faculties-association",
                pageId: PagesID["corporate.editions.tutors-faculties"],
                service:
                  ServicesURL.faculties + `?corporate_id=${id}&role=TUTOR`,
              }
            : value,
        );
        break;
      case "site":
        setOpenAssociations(
          value
            ? {
                accessorKey: "site",
                mediaType: "site",
                type: "table",
                titleKey: "site-association",
                pageId: PagesID["corporate.locations"],
                service: `${ServicesURL.sites.replace(
                  "{corporateId}",
                  `${id}`,
                )}`,
                multiChoice: false,
                defaultFilters: { bookable: true },
                manageConfigs: {
                  bookable: {
                    optionsToDisable: ["false"],
                  },
                },
              }
            : value,
        );
        break;
      case "rooms":
        setOpenAssociations(
          value
            ? {
                accessorKey: "rooms",
                mediaType: "rooms",
                type: "table",
                titleKey: "room-association",
                pageId: PagesID["corporate.locations.rooms"],
                service: `${ServicesURL.rooms.replace(
                  "{corporateId}",
                  `${id}`,
                )}`,
                defaultFilters: {
                  siteId: siteAssociation[0] && siteAssociation[0]?.id,
                  bookable: true,
                },
                manageConfigs: {
                  bookable: {
                    optionsToDisable: ["false"],
                  },
                },
              }
            : value,
        );
        break;
    }
  };

  const getValues = (accessorKey: string) => {
    switch (accessorKey) {
      case "learning_objects":
        return activitiesAssociation;
      case "faculties":
        return facultiesAssociation;
      case "site":
        return siteAssociation;
      case "rooms":
        return roomAssociation;
      case "tutors":
        return tutorsAssociation;
    }
  };

  const handleDelete = (accessorKey: string, e: any) => {
    switch (accessorKey) {
      case "learning_objects":
        setActivitiesAssociation(
          activitiesAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "faculties":
        setFacultiesAssociation(
          facultiesAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "tutors":
        setTutorsAssociation(
          tutorsAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "site":
        setSiteAssociation(
          siteAssociation.filter((element: any) => element.id !== e.id),
        );
        setRoomAssociation([]);
        break;
      case "rooms":
        setRoomAssociation(
          roomAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
    }
  };

  const handleAssociation = (accessorKey: string, value: any) => {
    switch (accessorKey) {
      case "learning_objects":
        const objToActivitiesAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return activitiesAssociation.find(
                (elem: any) => elem.id === +key,
              );
            }
          },
        );
        setActivitiesAssociation(objToActivitiesAssociation);
        break;
      case "faculties":
        const objToFacultiesAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return facultiesAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setFacultiesAssociation(objToFacultiesAssociation);
        break;
      case "tutors":
        const objToTutorsAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return tutorsAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setTutorsAssociation(objToTutorsAssociation);
        break;
      case "site":
        const objToSiteAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return siteAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setSiteAssociation(objToSiteAssociation);
        break;
      case "rooms":
        const objToRoomAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return roomAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setRoomAssociation(objToRoomAssociation);
        break;
    }
  };

  const onSubmit = async (
    inputForm: any,
    sessions: SessionGetInterface[],
    isDiversityConfirmation: boolean = false,
  ) => {
    let newInputForm = {
      lo_id:
        activitiesAssociation.length > 0
          ? parseInt(activitiesAssociation[0].id)
          : activitiesAssociation,
      edition_code: inputForm.edition_code || null,
      edition_name: inputForm?.edition_name || null,
      publish_date: dayjs(inputForm.publish_date).format("YYYY-MM-DD"),
      expected_completion_time: inputForm.expected_completion_time || 0,
      sessions: sessions.map((session: any) => {
        return {
          ...session,
          publish_date_session: dayjs(session.publish_date_session).format(
            "YYYY-MM-DD",
          ),
          start_date: dayjs(session.start_date).format("YYYY-MM-DDTHH:mm:ssZ"),
          end_date: dayjs(session.end_date).format("YYYY-MM-DDTHH:mm:ssZ"),
          site: session.site.length
            ? Number(session.site[0].id)
            : session.site.id,
          rooms: !!session.rooms
            ? session.rooms.map((room: { id: string; label: string }) => {
                return Number(room.id);
              })
            : [],
          faculties: !!session.faculties
            ? session.faculties.map(
                (faculty: { id: string; label: string }) => {
                  return Number(faculty.id);
                },
              )
            : [],
          tutors: !!session.tutors
            ? session.tutors.map((tutor: { id: string; label: string }) => {
                return Number(tutor.id);
              })
            : [],
          "QR-code": isTrueValue(session["QR-code"]) || false,
          confirmed: isTrueValue(session["confirmed"]) || false,
        };
      }),
    };

    const sessionsDuration = sessions.reduce((total, obj) => {
      const start: any = new Date(obj.start_date);
      const end: any = new Date(obj.end_date);
      const duration = (end - start) / (1000 * 60);
      return total + Math.abs(duration);
    }, 0);

    if (
      isDiversityConfirmation === undefined &&
      activityDuration != null &&
      sessionsDuration != activityDuration
    ) {
      setHasDifferentDuration(true);
      setSavedInputForm(inputForm);
      setSavedSessions(sessions);
      return;
    }

    setHasDifferentDuration(false);
    setSavedInputForm(null);
    setSavedSessions([]);

    setOpenConfirmStatus({ s: StatusEnum.LOADING });

    try {
      await triggetEdition(newInputForm);
      await Promise.all([mutate && mutate(), mutateSession && mutateSession()]);
      setOpenConfirmStatus({ s: StatusEnum.OK });
    } catch (error) {
      const errorMessage = isSessionOverlapError(error)
        ? formatSessionOccupiedError(error, t)
        : getAllErrorsAsString(error);
      setOpenConfirmStatus({
        s: StatusEnum.KO,
        message: errorMessage,
      });
    }
  };

  return {
    onSubmit,
    setOpenConfirmStatus,
    openConfirmStatus,
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetSessionAssociations,
    setSiteAssociation,
    setRoomAssociation,
    setFacultiesAssociation,
    setTutorsAssociation,
    openAssociations,
    siteAssociation,
    facultiesAssociation,
    roomAssociation,
    tutorsAssociation,
    setHasDifferentDuration,
    hasDifferentDuration,
    savedInputForm,
    savedSessions,
  };
};
